import { FormEvent, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ChevronLeft,
  RefreshCw,
  Shield,
  UserPlus,
  Trash2,
  CheckCircle2,
  AlertCircle,
  Users,
  X,
  Mail,
} from "lucide-react";

import {
  useAddCommitteeMemberMutation,
  useGetCommitteeDetailsQuery,
  useRemoveCommitteeMemberMutation,
  useUpdateCommitteeMutation,
} from "../api/committeesApi";

import MemberPicker from "../components/MemberPicker";
import RolePrivilegesEditor from "../../access/components/RolePrivilegesEditor";
import { PERMS } from "../../../utils/perms";
import AccessGate from "../../../components/AccessGate";
import PageGate from "../../../components/PageGate";

type UiMsg = { type: "success" | "error"; text: string } | null;

export default function CommitteeDetailsPage() {
  const { id } = useParams();
  const committeeId = Number(id);

  const { data, isLoading, refetch } = useGetCommitteeDetailsQuery(
    committeeId,
    { skip: !committeeId }
  );
  const [updateCommittee, { isLoading: saving }] = useUpdateCommitteeMutation();
  const [addMember, { isLoading: adding }] = useAddCommitteeMemberMutation();
  const [removeMember] = useRemoveCommitteeMemberMutation();

  const [msg, setMsg] = useState<UiMsg>(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  const [edit, setEdit] = useState({
    name: "",
    description: "",
    is_active: true,
  });
  const [pick, setPick] = useState({
    member_id: "" as string | null, // UUID from picker
    user_id: null as number | null, // for Picker's valueUserId
    committee_role: "member" as "chairman" | "secretary" | "member",
    position: "",
  });

  useEffect(() => {
    if (data) {
      setEdit({
        name: data.name ?? "",
        description: data.description ?? "",
        is_active: Boolean(data.is_active),
      });
    }
  }, [data]);

  const members = useMemo(() => data?.members ?? [], [data]);

  const onUpdateSettings = async (e: FormEvent) => {
    e.preventDefault();
    setMsg(null);
    try {
      await updateCommittee({ id: committeeId, ...edit }).unwrap();
      setMsg({ type: "success", text: "Committee updated successfully" });
      refetch();
    } catch (e: any) {
      setMsg({ type: "error", text: e?.data?.message || "Failed to update" });
    }
  };

  const onConfirmAdd = async () => {
    if (!pick.member_id) return;
    setMsg(null);
    try {
      await addMember({
        committeeId,
        body: {
          member_id: pick.member_id,
          committee_role: pick.committee_role,
          position: pick.position?.trim() || undefined,
        },
      }).unwrap();

      setMsg({ type: "success", text: "Member added to committee" });
      setIsAddModalOpen(false);
      setPick({
        member_id: null,
        user_id: null,
        committee_role: "member",
        position: "",
      });
      refetch();
    } catch (e: any) {
      setMsg({ type: "error", text: e?.data?.message || "Enrollment failed" });
    }
  };

  const onRemoveMember = async (memberId: string, name: string) => {
    if (!window.confirm(`Remove ${name} from this committee?`)) return;
    try {
      await removeMember({ committeeId, memberId }).unwrap();
      setMsg({ type: "success", text: "Member removed" });
      refetch();
    } catch (e: any) {
      setMsg({ type: "error", text: "Failed to remove member" });
    }
  };

  if (isLoading)
    return (
      <div className="p-20 text-center font-black text-gray-300 animate-pulse tracking-widest">
        LOADING...
      </div>
    );

  return (
    <PageGate perm={PERMS.committee.access}>
      <div className="space-y-6 pb-20">
        {/* Navigation */}
        <div className="flex items-center justify-between">
          <Link
            to="/committees"
            className="group flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-gray-400 hover:text-gray-900 transition-colors"
          >
            <ChevronLeft className="w-4 h-4" /> Back to List
          </Link>
          <div className="flex gap-3">
            <button
              onClick={() => refetch()}
              className="p-2 border rounded-xl hover:bg-gray-50 transition-all active:scale-95 shadow-sm"
            >
              <RefreshCw
                className={`w-4 h-4 text-gray-500 ${
                  isLoading ? "animate-spin" : ""
                }`}
              />
            </button>
            <AccessGate perm={PERMS.committee.manage_members}>
              <button
                onClick={() => setIsAddModalOpen(true)}
                className="flex items-center gap-2 px-6 py-2 bg-gray-900 text-white rounded-xl font-bold text-xs shadow-lg shadow-gray-900/20 hover:bg-gray-800 transition-all active:scale-95"
              >
                <UserPlus className="w-4 h-4" /> ENROLL MEMBER
              </button>
            </AccessGate>
          </div>
        </div>

        {/* Header Card */}
        <div className="bg-white border rounded-2xl p-6 shadow-sm border-b-4 border-b-gray-900">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div
                className={`p-4 rounded-2xl ${
                  data?.is_active
                    ? "bg-indigo-50 text-indigo-600"
                    : "bg-gray-50 text-gray-400"
                }`}
              >
                <Users className="w-8 h-8" />
              </div>
              <div>
                <h1 className="text-2xl font-black text-gray-900 tracking-tight leading-none mb-2">
                  {data?.name}
                </h1>
                <div className="flex items-center gap-3">
                  <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest flex items-center gap-1.5">
                    <Shield className="w-3.5 h-3.5 text-indigo-500" />{" "}
                    {data?.role?.name || "Standard"}
                  </span>
                  <div
                    className={`w-2 h-2 rounded-full ${
                      data?.is_active ? "bg-green-500" : "bg-gray-300"
                    }`}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {msg && (
          <div
            className={`p-4 rounded-xl border flex justify-between items-center animate-in slide-in-from-top-2 ${
              msg.type === "success"
                ? "bg-green-50 border-green-100 text-green-800"
                : "bg-red-50 border-red-100 text-red-800"
            }`}
          >
            <div className="flex items-center gap-2 font-bold text-sm">
              {msg.type === "success" ? (
                <CheckCircle2 className="w-4 h-4" />
              ) : (
                <AlertCircle className="w-4 h-4" />
              )}
              {msg.text}
            </div>
            <button onClick={() => setMsg(null)}>
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Members Table */}
          <div className="lg:col-span-2 bg-white border rounded-2xl overflow-hidden shadow-sm">
            <div className="px-6 py-4 bg-gray-50/50 border-b flex justify-between items-center">
              <h2 className="font-black text-[10px] uppercase tracking-[0.2em] text-gray-400">
                Committee Roster
              </h2>
              <span className="text-[10px] font-black text-gray-400">
                {members.length} MEMBERS
              </span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="text-[10px] font-black uppercase text-gray-400 tracking-widest border-b">
                    <th className="py-4 px-6">Name</th>
                    <th className="py-4 px-6">Role / Position</th>
                    <th className="py-4 px-6 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {members.map((m: any) => (
                    <tr
                      key={m.id}
                      className="group hover:bg-gray-50/80 transition-colors text-sm"
                    >
                      <td className="py-4 px-6">
                        <div className="font-bold text-gray-900">
                          {m.member?.surname} {m.member?.firstname}
                        </div>
                        <div className="text-[11px] text-gray-400 flex items-center gap-1 mt-0.5 font-medium italic">
                          <Mail className="w-3 h-3" /> {m.member?.user?.email}
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <span
                          className={`inline-block px-2 py-0.5 rounded-md text-[10px] font-black uppercase border mb-1 
                          ${
                            m.committee_role === "chairman"
                              ? "bg-blue-50 border-blue-100 text-blue-600"
                              : m.committee_role === "secretary"
                              ? "bg-purple-50 border-purple-100 text-purple-600"
                              : "bg-gray-100 border-gray-200 text-gray-500"
                          }`}
                        >
                          {m.committee_role}
                        </span>
                        <div className="text-[10px] text-gray-400 font-bold uppercase tracking-tight">
                          {m.position || "N/A"}
                        </div>
                      </td>
                      <td className="py-4 px-6 text-right">
                        <AccessGate perm={PERMS.committee.manage_members}>
                          <button
                            onClick={() =>
                              onRemoveMember(m.member_id, m.member?.surname)
                            }
                            className="p-2 text-gray-300 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all opacity-0 group-hover:opacity-100"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </AccessGate>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Settings Column */}
          <div className="space-y-6">
            <section className="bg-white border rounded-2xl p-6 shadow-sm">
              <h2 className="font-black text-[10px] uppercase tracking-[0.2em] text-gray-400 mb-6">
                Committee Details
              </h2>
              <form onSubmit={onUpdateSettings} className="space-y-4">
                <div>
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1 block">
                    Title
                  </label>
                  <input
                    value={edit.name}
                    onChange={(e) => setEdit({ ...edit, name: e.target.value })}
                    className="w-full border-b-2 border-gray-100 py-2 outline-none focus:border-gray-900 transition-all font-bold text-sm bg-transparent"
                  />
                </div>
                <div>
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1 block">
                    Description
                  </label>
                  <textarea
                    value={edit.description}
                    onChange={(e) =>
                      setEdit({ ...edit, description: e.target.value })
                    }
                    rows={3}
                    className="w-full border-2 border-gray-50 rounded-xl p-3 outline-none focus:border-gray-900 transition-all text-xs bg-gray-50/30 resize-none font-medium"
                  />
                </div>
                <label className="flex items-center gap-3 p-3 bg-gray-50/50 rounded-xl cursor-pointer hover:bg-gray-100 transition-colors">
                  <input
                    type="checkbox"
                    checked={edit.is_active}
                    onChange={(e) =>
                      setEdit({ ...edit, is_active: e.target.checked })
                    }
                    className="w-4 h-4 rounded border-gray-300 text-gray-900"
                  />
                  <span className="text-[11px] font-black text-gray-600 uppercase tracking-widest">
                    Active Status
                  </span>
                </label>
                <AccessGate perm={PERMS.committee.update}>
                  <button
                    type="submit"
                    disabled={saving}
                    className="w-full py-3 bg-gray-900 text-white rounded-xl font-black text-[10px] uppercase tracking-[0.2em] shadow-lg shadow-gray-900/20"
                  >
                    {saving ? "SAVING..." : "SAVE CHANGES"}
                  </button>
                </AccessGate>
              </form>
            </section>
          </div>
        </div>

        {/* RBAC Mapping */}
        <section className="pt-6">
          <PageGate perm={PERMS.committee.managePermission}>
            {data?.role_id && <RolePrivilegesEditor roleId={data.role_id} />}
          </PageGate>
        </section>

        {/* --- ENROLLMENT MODAL --- */}
        {isAddModalOpen && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div
              className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm"
              onClick={() => setIsAddModalOpen(false)}
            />
            <div className="relative w-full max-w-lg bg-white rounded-3xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
              <div className="p-6 border-b flex justify-between items-center bg-gray-50/50">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-indigo-600 rounded-lg text-white shadow-lg shadow-indigo-600/20">
                    <UserPlus className="w-5 h-5" />
                  </div>
                  <h2 className="text-xl font-black text-gray-900 tracking-tight">
                    Enroll Member
                  </h2>
                </div>
                <button
                  onClick={() => setIsAddModalOpen(false)}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>

              <div className="p-6 space-y-6">
                {/* Using MemberPicker here */}
                <MemberPicker
                  label="1. Identity Search"
                  valueUserId={pick.user_id}
                  onPickUserId={(uid) =>
                    setPick((p) => ({ ...p, user_id: uid }))
                  }
                  onPick={(m) =>
                    setPick((p) => ({
                      ...p,
                      member_id: m ? (m.member_id ?? m.id) : null,
                    }))
                  }
                />

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2 block">
                      2. Committee Role
                    </label>
                    <select
                      value={pick.committee_role}
                      onChange={(e) =>
                        setPick({
                          ...pick,
                          committee_role: e.target.value as any,
                        })
                      }
                      className="w-full border-b-2 border-gray-100 py-3 outline-none focus:border-indigo-600 font-bold text-xs bg-transparent uppercase"
                    >
                      <option value="member">General Member</option>
                      <option value="chairman">Chairman</option>
                      <option value="secretary">Secretary</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2 block">
                      3. Position
                    </label>
                    <input
                      value={pick.position}
                      onChange={(e) =>
                        setPick({ ...pick, position: e.target.value })
                      }
                      placeholder="e.g. Lead Auditor"
                      className="w-full border-b-2 border-gray-100 py-3 outline-none focus:border-indigo-600 text-sm font-bold bg-transparent"
                    />
                  </div>
                </div>

                <div className="pt-4 flex gap-3">
                  <button
                    onClick={() => setIsAddModalOpen(false)}
                    className="flex-1 py-4 border rounded-2xl font-black text-[10px] uppercase tracking-[0.2em] text-gray-400 hover:bg-gray-50 transition-colors"
                  >
                    CANCEL
                  </button>
                  <button
                    onClick={onConfirmAdd}
                    disabled={adding || !pick.member_id}
                    className="flex-1 py-4 bg-indigo-600 text-white rounded-2xl font-black text-[10px] uppercase tracking-[0.2em] shadow-xl shadow-indigo-600/30 hover:bg-indigo-700 disabled:opacity-50 transition-all flex items-center justify-center gap-2"
                  >
                    {adding ? (
                      <RefreshCw className="w-3 h-3 animate-spin" />
                    ) : (
                      <CheckCircle2 className="w-3 h-3" />
                    )}
                    CONFIRM ENROLLMENT
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </PageGate>
  );
}
